import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Instagram, ExternalLink, Play, Images } from "lucide-react";
import { pub } from "../lib/crmClient";

const IG_URL = "https://www.instagram.com/paint_king/";

function PostTile({ post, i }) {
    const isVideo = post.media_type === "VIDEO";
    const isCarousel = post.media_type === "CAROUSEL_ALBUM";
    const img = isVideo ? post.thumbnail_url : post.media_url;
    return (
        <motion.a
            href={post.permalink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`ig-post-${post.id}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, delay: (i % 6) * 0.06, ease: [0.19, 1, 0.22, 1] }}
            className="group block relative aspect-square overflow-hidden border border-white/10 bg-black"
        >
            {img ? (
                <img src={img} alt={post.caption?.slice(0, 120) || "PaintKing project"} loading="lazy"
                    className="w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.06]"/>
            ) : (
                <div className="w-full h-full flex items-center justify-center font-mono text-[10px] tracking-[0.28em] uppercase text-white/40">preview unavailable</div>
            )}
            <div className="absolute top-3 right-3 flex gap-1.5">
                {isVideo && (
                    <span className="glass-chip border border-white/40 px-2 py-1 font-mono text-[9px] tracking-[0.28em] uppercase flex items-center gap-1">
                        <Play className="w-3 h-3" /> {post.media_product_type === "REELS" ? "Reel" : "Video"}
                    </span>
                )}
                {isCarousel && (
                    <span className="glass-chip border border-white/40 px-2 py-1 font-mono text-[9px] tracking-[0.28em] uppercase flex items-center gap-1">
                        <Images className="w-3 h-3" /> Carousel
                    </span>
                )}
                {post.pinned && (
                    <span className="bg-[#FFE600] text-black px-2 py-1 font-mono text-[9px] tracking-[0.28em] uppercase">★ Featured</span>
                )}
            </div>
            {post.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/80 to-transparent p-4 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                    <p className="font-mono text-[11px] leading-relaxed text-white/90 line-clamp-3">{post.caption}</p>
                </div>
            )}
        </motion.a>
    );
}

export function InstagramPortfolio() {
    const [posts, setPosts] = useState([]);
    const [state, setState] = useState({ connected: false, loading: true });

    useEffect(() => {
        pub.get("/instagram/posts?limit=6").then((r) => {
            setPosts(r.data.posts || []);
            setState({ connected: r.data.connected, loading: false });
        }).catch(() => setState({ connected: false, loading: false }));
    }, []);

    return (
        <section id="instagram" data-testid="instagram-portfolio" className="relative bg-black py-24 md:py-40 border-t border-white/10 overflow-hidden">
            <div className="px-6 md:px-16">
                <div className="grid grid-cols-12 gap-6 mb-12 md:mb-16 items-end">
                    <div className="col-span-12 md:col-span-7">
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">[ Ch. 06 · Instagram ]</div>
                        <h2 className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] tracking-tight mt-4">
                            Fresh paint.
                            <br />
                            <span className="rainbow-text">Real projects.</span>
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-5">
                        <p className="font-display text-lg md:text-xl leading-tight text-white/85 max-w-md">
                            See what we've been painting. Follow Paint King for home transformations, commercial projects, beautiful finishes and behind-the-scenes work.
                        </p>
                        <a
                            href={IG_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="ig-follow-cta"
                            className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.28em] uppercase border-b border-white/40 hover:border-[#FFE600] hover:text-[#FFE600] pb-1 transition-colors"
                        >
                            <Instagram className="w-4 h-4" /> Follow @paint_king on Instagram <ExternalLink className="w-3 h-3" />
                        </a>
                    </div>
                </div>

                {state.loading ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                        {[...Array(6)].map((_, i) => <div key={i} className="aspect-square bg-white/[0.03] border border-white/10 animate-pulse" />)}
                    </div>
                ) : !state.connected || posts.length === 0 ? (
                    <ConnectEmptyState />
                ) : (
                    <div data-testid="ig-grid" className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                        {posts.map((p, i) => <PostTile key={p.id} post={p} i={i} />)}
                    </div>
                )}

                {/* View more + Quote CTA */}
                <div className="mt-16 flex flex-col items-center gap-10">
                    <Link to="/our-work" data-testid="ig-view-more"
                        className="group relative overflow-hidden border border-white/80 hover:border-[#FFE600] px-10 py-4 font-mono text-[11px] md:text-sm tracking-[0.3em] uppercase">
                        <span className="relative z-10 group-hover:text-black transition-colors">View More Of Our Work →</span>
                        <span className="absolute inset-0 bg-[#FFE600] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
                    </Link>

                    <div data-testid="ig-quote-cta" className="w-full max-w-3xl glass border border-white/15 p-8 md:p-10 text-center">
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFE600]">Inspired by what you see?</div>
                        <h3 className="font-display uppercase text-3xl md:text-5xl leading-tight tracking-tight mt-3">
                            Give your home, office, or commercial building the <span className="rainbow-text">royal treatment.</span>
                        </h3>
                        <a href="#whatsapp" data-testid="ig-quote-btn"
                            className="mt-6 inline-flex items-center gap-3 border border-[#FFE600] bg-[#FFE600]/[0.06] hover:bg-[#FFE600] hover:text-black px-8 py-4 font-mono text-xs md:text-sm tracking-[0.28em] uppercase transition-colors">
                            Get A Free Painting Quote →
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ConnectEmptyState() {
    return (
        <div data-testid="ig-empty" className="glass border border-white/15 p-10 md:p-16 text-center">
            <Instagram className="w-10 h-10 mx-auto text-[#FFE600]" strokeWidth={1.4} />
            <h3 className="font-display uppercase text-3xl md:text-4xl leading-tight tracking-tight mt-4">
                Portfolio warming up.
            </h3>
            <p className="font-mono text-[12px] md:text-[13px] text-white/60 mt-3 max-w-xl mx-auto leading-relaxed">
                Paul is connecting the Instagram feed. In the meantime, head over to see every project on the real deal.
            </p>
            <a href="https://www.instagram.com/paint_king/" target="_blank" rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.28em] uppercase border border-white/40 hover:border-[#FFE600] hover:text-[#FFE600] px-5 py-3 transition-colors">
                <Instagram className="w-4 h-4" /> Open @paint_king
            </a>
        </div>
    );
}
